import type {
  StoryResponse,
  MatchResponse,
  MatchConfig,
  DebugStoryResult,
  MatchDebugInfo,
  RescoreResult,
  StatsResponse
} from './types';

// Use proxy in dev, direct URL in prod
const API_BASE = import.meta.env.DEV
  ? '/api'
  : 'https://pebbl-api.gogjhan.workers.dev';

async function fetchApi<T>(
  path: string,
  options?: RequestInit
): Promise<T> {
  const url = `${API_BASE}${path}`;
  const res = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options?.headers,
    },
  });

  if (!res.ok) {
    const error = await res.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(error.error || error.message || `API error: ${res.status}`);
  }

  return res.json();
}

// ==================== Stories ====================

export async function listStories(limit = 50, offset = 0) {
  return fetchApi<{ stories: StoryResponse[]; pagination: { limit: number; offset: number; count: number } }>(
    `/admin/stories?limit=${limit}&offset=${offset}`
  );
}

export async function getStory(id: string) {
  return fetchApi<StoryResponse & { youProfile?: any; frequency?: any; emotionalTags?: string[] }>(
    `/admin/stories/${id}`
  );
}

export async function getStoryWithMatches(id: string) {
  return fetchApi<{ story: StoryResponse; matches: MatchResponse[] }>(
    `/stories/${id}`
  );
}

export async function deleteStory(id: string) {
  return fetchApi<{ success: boolean; deleted: string }>(
    `/admin/stories/${id}`,
    { method: 'DELETE' }
  );
}

// ==================== Matches ====================

export async function getMatch(id: string) {
  return fetchApi<MatchResponse>(`/matches/${id}`);
}

export async function getMatchDebug(id: string) {
  return fetchApi<MatchDebugInfo>(`/admin/matches/${id}/debug`);
}

export async function rescoreMatch(id: string, config: Partial<MatchConfig>) {
  return fetchApi<RescoreResult>(`/admin/matches/${id}/rescore`, {
    method: 'POST',
    body: JSON.stringify({ config }),
  });
}

// ==================== Debug ====================

export async function submitDebugStory(
  rawText: string,
  city: string,
  config?: Partial<MatchConfig>,
  skipPersist = false
) {
  return fetchApi<DebugStoryResult>('/admin/debug/stories', {
    method: 'POST',
    body: JSON.stringify({ rawText, city, config, skipPersist }),
  });
}

// ==================== Config ====================

export async function getConfig() {
  return fetchApi<{ config: MatchConfig; description: Record<string, any> }>('/admin/config');
}

// ==================== Stats ====================

export async function getStats() {
  return fetchApi<StatsResponse>('/admin/stats');
}
